(() => {
  const w = window;
  const d = document;
  const urls = w.luxoAssetUrls || {};

  const loadScript = (src, attrs = { defer: true }) =>
    w.luxoLoadScriptOnce ? w.luxoLoadScriptOnce(src, attrs) : Promise.resolve(null);

  const cartStylePromises = {};
  const loadStyle = (href) => {
    if (!href) return Promise.resolve(null);

    const normalizedHref = new URL(href, d.baseURI).href;
    if (cartStylePromises[normalizedHref]) return cartStylePromises[normalizedHref];

    cartStylePromises[normalizedHref] = new Promise((resolve) => {
      const existingLink = Array.from(d.querySelectorAll('link[href]')).find((link) =>
        link.href === normalizedHref || link.getAttribute('href') === href
      );
      const stylesheetReady = () =>
        Array.from(d.styleSheets).some((sheet) => sheet.href === normalizedHref);

      if (existingLink?.dataset.loaded === 'true' || stylesheetReady()) {
        if (existingLink?.media === 'print') existingLink.media = 'all';
        resolve(existingLink || null);
        return;
      }

      const link = existingLink || d.createElement('link');
      let settled = false;

      let readyCheck = null;
      let fallbackTimer = null;
      const settle = (value) => {
        if (settled) return;
        settled = true;
        if (readyCheck) clearInterval(readyCheck);
        if (fallbackTimer) clearTimeout(fallbackTimer);
        link.dataset.loaded = 'true';
        if (link.media === 'print') link.media = 'all';
        resolve(value);
      };

      link.addEventListener('load', () => settle(link), { once: true });
      link.addEventListener('error', () => settle(null), { once: true });
      link.rel = 'stylesheet';

      if (!existingLink) {
        link.href = href;
        d.head.appendChild(link);
      } else if (link.media === 'print') {
        link.media = 'all';
      }

      const checkReady = () => {
        if (link.sheet || stylesheetReady()) {
          settle(link);
        }
      };

      checkReady();
      if (!settled) {
        readyCheck = setInterval(checkReady, 50);
        fallbackTimer = setTimeout(() => settle(link), 3000);
      }
    });

    return cartStylePromises[normalizedHref];
  };

  let cartStylesReady = w.luxoCartStylesReady === true;
  let cartStylesPromise = null;
  const loadCartStyles = () => {
    if (cartStylesReady) return Promise.resolve([]);

    if (!cartStylesPromise) {
      cartStylesPromise = Promise.all((urls.cartStyles || []).map(loadStyle))
        .catch((error) => {
          console.warn('Unable to load cart drawer styles:', error);
          return [];
        })
        .finally(() => {
          cartStylesReady = true;
          w.luxoCartStylesReady = true;
        });
    }

    return cartStylesPromise;
  };
  w.luxoLoadCartStyles = w.luxoLoadCartStyles || loadCartStyles;

  w.luxoLoadPredictiveSearch = w.luxoLoadPredictiveSearch || (() => {
    if (customElements.get('predictive-search')) return Promise.resolve(customElements.get('predictive-search'));
    return loadScript(urls.predictiveSearch).then(() => customElements.whenDefined('predictive-search'));
  });

  let cartDrawerAssetsPromise = null;
  const loadCartDrawerAssets = () => {
    if (cartDrawerAssetsPromise) return cartDrawerAssetsPromise;

    const cartCore = customElements.get('cart-remove-button') || w.fetchCartData
      ? Promise.resolve()
      : loadScript(urls.cart);

    const cartScripts = cartCore
      .then(() =>
        customElements.get('quantity-popover')
          ? Promise.resolve()
          : loadScript(urls.quantityPopover)
      )
      .then(() =>
        customElements.get('cart-drawer')
          ? Promise.resolve()
          : loadScript(urls.cartDrawer)
      )
      .then(() => customElements.whenDefined('cart-drawer'));
    cartDrawerAssetsPromise = Promise.all([loadCartStyles(), cartScripts]).catch((error) => {
      cartDrawerAssetsPromise = null;
      throw error;
    });

    return cartDrawerAssetsPromise;
  };

  w.luxoLoadCartDrawer = w.luxoLoadCartDrawer || (() => {
    const drawer = d.querySelector('cart-drawer');
    if (!drawer) return Promise.resolve(null);

    if (customElements.get('cart-drawer') && typeof drawer.open === 'function') {
      return loadCartStyles().then(() => d.querySelector('cart-drawer'));
    }

    return loadCartDrawerAssets().then(() => d.querySelector('cart-drawer'));
  });

  const loadCartCouponField = () => {
    if (customElements.get('cart-coupon-field')) return Promise.resolve();
    return loadScript(urls.cartCouponField);
  };

  w.luxoLoadCartDrawerOnIntent = w.luxoLoadCartDrawerOnIntent || ((triggeredBy) => {
    return w.luxoLoadCartDrawer().then((drawer) => {
      loadCartCouponField();
      if (triggeredBy && drawer && typeof drawer.open === 'function') drawer.open(triggeredBy);
      return drawer;
    });
  });

  let cartUpdateDrawerPromise = null;
  let cartUpdateFallbackTimer = null;
  const renderCartDrawerFromUpdate = (drawer, event = {}) => {
    const cartData = event.cartData;
    if (cartData?.sections?.['cart-drawer'] && typeof drawer.renderContents === 'function') {
      drawer.renderContents(cartData);
      return Promise.resolve();
    }

    if (typeof drawer.fetchCartDrawerContent === 'function') {
      return drawer.fetchCartDrawerContent();
    }

    if (typeof drawer.open === 'function') drawer.open();
    return Promise.resolve();
  };
  const isCartDrawerOpenOrQueued = () => {
    const drawer = d.querySelector('cart-drawer');
    return drawer?.classList.contains('active') || drawer?.cartStylesOpenQueued;
  };

  const loadAndOpenCartDrawerFromUpdate = (event = {}) => {
    if (cartUpdateDrawerPromise) return cartUpdateDrawerPromise;

    cartUpdateDrawerPromise = w.luxoLoadCartDrawer()
      .then((drawer) => {
        if (!drawer) return null;
        loadCartCouponField();
        return renderCartDrawerFromUpdate(drawer, event);
      })
      .catch((error) => {
        console.warn('Unable to open cart drawer after cart update:', error);
      })
      .finally(() => {
        cartUpdateDrawerPromise = null;
      });

    return cartUpdateDrawerPromise;
  };
  const openCartDrawerFromUpdate = (event = {}) => {
    if (!d.querySelector('cart-drawer')) return;
    if (event.source === 'cart-items') return;
    // The post-add cross-sell modal refreshes the drawer itself and owns the screen while
    // it is open - opening the drawer on top of it would stack two overlays.
    if (event.source === 'add-on-modal') return;
    if (d.querySelector('add-on-modal[open]')) return;

    if (cartUpdateFallbackTimer) clearTimeout(cartUpdateFallbackTimer);
    cartUpdateFallbackTimer = setTimeout(() => {
      cartUpdateFallbackTimer = null;
      if (!isCartDrawerOpenOrQueued()) loadAndOpenCartDrawerFromUpdate(event);
    }, 350);
  };

  const bindCartDrawerCartUpdate = () => {
    if (!d.querySelector('cart-drawer')) return;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, openCartDrawerFromUpdate);
  };

  const bindCartDrawerIntent = () => {
    if (!d.querySelector('cart-drawer')) return;

    d.addEventListener('click', (event) => {
      const trigger = event.target.closest('#cart-icon-bubble, [data-open-cart]');
      if (trigger && !customElements.get('cart-drawer')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        w.luxoLoadCartDrawerOnIntent(trigger);
        return;
      }

      if (trigger || event.target.closest('cart-drawer')) loadCartStyles();
    }, true);

    d.addEventListener('keydown', (event) => {
      if (customElements.get('cart-drawer')) return;
      const key = event.key || event.code;
      if (key !== 'Enter' && key !== ' ' && key !== 'Space' && key !== 'Spacebar') return;

      const trigger = event.target.closest('#cart-icon-bubble, [data-open-cart]');
      if (!trigger) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      w.luxoLoadCartDrawerOnIntent(trigger);
    }, true);

    const observer = new MutationObserver(() => {
      if (!d.querySelector('cart-drawer[open], cart-drawer.is-open, cart-drawer.active')) return;
      loadCartStyles();
      observer.disconnect();
    });
    observer.observe(d.documentElement, { childList: true, subtree: true });

    const cartIcon = d.getElementById('cart-icon-bubble');
    if (cartIcon) {
      cartIcon.addEventListener('mouseover', function warmCartDrawer() {
        w.luxoLoadCartDrawerOnIntent();
        cartIcon.removeEventListener('mouseover', warmCartDrawer);
      }, { passive: true });
    }
  };

  const bindPredictiveSearchIntent = () => {
    if (!d.querySelector('predictive-search')) return;
    let requested = false;

    const replayActiveInput = () => {
      const active = d.activeElement;
      if (!active || !active.matches('predictive-search input[type="search"]')) return;
      if (active.value) active.dispatchEvent(new Event('input', { bubbles: true }));
    };

    const requestPredictiveSearch = () => {
      if (requested) return;
      requested = true;
      w.luxoLoadPredictiveSearch().then(replayActiveInput);
    };

    d.addEventListener('focusin', (event) => {
      if (event.target.closest('predictive-search')) requestPredictiveSearch();
    });
    d.addEventListener('input', (event) => {
      if (event.target.closest('predictive-search')) requestPredictiveSearch();
    });
    d.addEventListener('pointerdown', (event) => {
      if (event.target.closest('predictive-search, .header-search')) requestPredictiveSearch();
    }, { passive: true });
  };

  const bindFooterKlaviyoNewsletter = () => {
    if (!w.luxoRunNearElement || !d.querySelector('klaviyo-newsletter')) return;
    w.luxoRunNearElement('klaviyo-newsletter', () => {
      loadScript(urls.klaviyoNewsletter);
    }, { rootMargin: '400px', fallbackDelay: 30000 });
  };

  const init = () => {
    bindCartDrawerIntent();
    bindCartDrawerCartUpdate();
    bindPredictiveSearchIntent();
    bindFooterKlaviyoNewsletter();
  };

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
